import { useEffect, useState } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { useGet } from "@/lib/api";
import { SignalBadge } from "@/components/SignalBadge";
import { SignalPage } from "@/pages/SignalPage";
import { IndicatorsPage } from "@/pages/IndicatorsPage";
import { DriversPage } from "@/pages/DriversPage";
import { CalendarPage } from "@/pages/CalendarPage";
import { MlPage } from "@/pages/MlPage";
import { NotificationsBell } from "@/components/NotificationsBell";
import { SettingsDialog } from "@/components/SettingsDialog";
import { updateAvailable, applyUpdate } from "@/lib/update";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { AccountMenu } from "@/components/AccountMenu";
import { PreviewBanner } from "@/components/PreviewBanner";
import { SiteFooter } from "@/components/SiteFooter";
import { InstallBanner } from "@/components/InstallBanner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { fmt } from "@/lib/format";
import { Gauge, LayoutGrid, LineChart, CalendarDays, Brain, RefreshCw, Crown } from "lucide-react";

const AUTO_REFRESH_MS = 60_000;
const UPDATE_CHECK_MS = 5 * 60_000;
const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "");

const TABS = [
  { key: "signal", label: "Signal", icon: Gauge },
  { key: "indicators", label: "Indicators", icon: LayoutGrid },
  { key: "drivers", label: "Drivers", icon: LineChart },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "accuracy", label: "ML", icon: Brain },
];

function App() {
  const [tab, setTab] = useState("signal");
  const [updateReady, setUpdateReady] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { data: signal, loading } = useGet("/signal", refreshKey);
  const { data: source } = useGet("/data-source", refreshKey);
  const { isPro } = useAuth();

  const refresh = () => setRefreshKey((k) => k + 1);
  const onDataChanged = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    const id = setInterval(() => { if (document.visibilityState === "visible") setRefreshKey((k) => k + 1); }, AUTO_REFRESH_MS);
    const check = () => updateAvailable().then((yes) => yes && setUpdateReady(true));
    const onVisible = () => { if (document.visibilityState === "visible") { setRefreshKey((k) => k + 1); check(); } };
    const uid = setInterval(() => { if (document.visibilityState === "visible") check(); }, UPDATE_CHECK_MS);
    check();
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(id); clearInterval(uid); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  return (
    <div className="App min-h-screen pb-24">
      {/* sticky glass header */}
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <PreviewBanner />
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0"
              style={{ background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.4)" }}>
              <span className="font-display font-extrabold text-[#F59E0B] text-sm">Au</span>
            </div>
            <div className="min-w-0">
              <div data-testid="app-title" className="font-display font-bold text-[13px] sm:text-sm leading-none whitespace-nowrap flex items-center gap-1.5">
                XAUUSD AI Terminal
                {source && (
                  <span data-testid="data-source-chip"
                    className="hidden sm:inline-block font-mono-num text-[8px] px-1.5 py-0.5 rounded-full leading-none"
                    style={{
                      color: source.live ? "#10B981" : "#9CA3AF",
                      background: source.live ? "rgba(16,185,129,0.12)" : "rgba(156,163,175,0.1)",
                      border: `1px solid ${source.live ? "#10B98155" : "#9CA3AF40"}`,
                    }}>
                    {source.live ? "● LIVE" : "SEEDED"}
                  </span>
                )}
              </div>
              {signal && (
                <div data-testid="main-header-gold-price" className="font-mono-num text-[11px] text-[#9CA3AF] mt-0.5 flex items-center gap-1.5">
                  ${fmt(signal.gold_price)}
                  {signal.feed && (
                    <span data-testid="quote-freshness" className="text-[9px]" style={{ color: signal.feed.live ? "#10B981" : "#F59E0B" }}
                      title={signal.feed.source}>
                      {signal.feed.live ? `spot · ${clock(signal.feed.quote_time)}` : "delayed ~10 min"}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {signal && <div className="hidden sm:flex items-center gap-1.5" title="Macro Master Signal (news-driven direction) — separate from the long-term position and the scalps"><span className="text-[9px] uppercase tracking-wider font-mono-num text-[#6B7280]">Macro</span><SignalBadge action={signal.action} confidence={signal.confidence} size="sm" testId="header-signal-badge" /></div>}
            {!isPro && (
              <UpgradeDialog trigger={
                <button data-testid="go-pro-button"
                  className="flex items-center gap-1 h-9 px-2.5 sm:px-3 rounded-lg bg-[#F59E0B] hover:bg-[#d98c08] text-black font-semibold text-xs transition-colors">
                  <Crown size={14} /> <span className="hidden sm:inline">Go Pro</span>
                </button>
              } />
            )}
            <NotificationsBell refreshKey={refreshKey} />
            <SettingsDialog onSaved={onDataChanged} />
            <AccountMenu />
            <button
              data-testid="refresh-button"
              onClick={refresh}
              className="hidden sm:grid w-9 h-9 rounded-lg place-items-center bg-white/5 hover:bg-white/10 transition-colors border border-white/10"
            >
              <RefreshCw size={15} className={`text-[#9CA3AF] ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </header>

      {/* content */}
      <main className="max-w-3xl mx-auto px-4 py-5">
        {tab === "signal" && <SignalPage signal={signal} loading={loading} refreshKey={refreshKey} />}
        {tab === "indicators" && <IndicatorsPage refreshKey={refreshKey} onDataChanged={onDataChanged} />}
        {tab === "drivers" && <DriversPage refreshKey={refreshKey} />}
        {tab === "calendar" && <CalendarPage refreshKey={refreshKey} onDataChanged={onDataChanged} />}
        {tab === "accuracy" && <MlPage refreshKey={refreshKey} />}
      </main>
      <SiteFooter />
      <InstallBanner />

      {/* bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-40 glass border-t border-white/10">
        <div className="max-w-3xl mx-auto px-2 py-2 grid grid-cols-5">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`nav-${t.key}`}
                onClick={() => setTab(t.key)}
                className="flex flex-col items-center gap-1 py-1.5 rounded-xl transition-colors"
              >
                <Icon size={20} className={active ? "text-[#F59E0B]" : "text-[#6B7280]"} strokeWidth={active ? 2.4 : 2} />
                <span className={`text-[10px] font-mono-num ${active ? "text-[#F59E0B]" : "text-[#6B7280]"}`}>{t.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <Toaster position="top-center" theme="dark" />
      {updateReady && (
        <button data-testid="update-banner" onClick={applyUpdate}
          className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-full bg-[#F59E0B] text-black text-xs font-semibold px-4 py-2 shadow-lg shadow-black/40 hover:bg-[#FBBF24] transition-colors">
          <RefreshCw size={13} /> New version available — tap to update
        </button>
      )}
    </div>
  );
}

export default function AppRoot() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}
